import {
    Component,
    computed,
    signal,
    Signal,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MatCardContent,
    MatCardHeader,
    MatCardModule,
} from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
    MatSlideToggleChange,
    MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Client } from 'src/app/models/client.model';
import { ClientService } from 'src/app/services/client.service';
import { TaskService } from 'src/app/services/task-service.service';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { UpsertTaskComponent } from '../../upsert-task/upsert-task.component';
import { Project } from 'src/app/models/project-model';
import { ACTIVE, INACTIVE } from 'src/app/constants/statusConstant';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import { COUNT, PROJECTID, USERS } from 'src/app/constants/queryParamConstants';
import { Page } from 'src/app/models/page.model';
import { Task } from 'src/app/models/task.model';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TaskUser } from 'src/app/interfaces/task-user.interface';
import { COUNT_ALL } from 'src/app/constants/searchConstants';

enum TaskAction {
    CREATE = 'CREATE',
    UPDATE = 'UPDATE',
    LOADING = 'LOADING',
    IDLE = 'IDLE',
}

@Component({
    selector: 'app-staff-utilization-view',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatCheckboxModule,
        MatSlideToggleModule,
        MatTableModule,
        MatIconModule,
        MatSort,
        MatSortModule,
        MatCardModule,
        MatOptionModule,
        MatSelectModule,
        MatCardModule,
        MatCardHeader,
        MatCardContent,
        MatProgressSpinner,
        MatMenuModule,
        MatTooltipModule,
        NgClass,
    ],
    templateUrl: './staff-utilization-view.component.html',
    styleUrl: './staff-utilization-view.component.scss',
})
export class StaffUtilizationViewComponent {
    @ViewChild(MatSort) sort: MatSort;
    private client!: Client;
    private _project: WritableSignal<Project | null> = signal<Project>(null);
    protected projectHoursRemaining: Signal<number | null> = computed(() => {
        if (this.project) {
            if (
                this.project.estimatedHours &&
                this.project.estimatedHours > 0
            ) {
                return (
                    this.project.estimatedHours -
                    this.project.totalProjectHours.hoursWorked
                );
            } else {
                return null;
            }
        } else {
            return null;
        }
    });
    protected calculatingHours: boolean = false;
    protected taskAction: TaskAction = TaskAction.IDLE;
    protected viewingActiveTasks = true;
    protected loading = false;
    protected displayedColumns: string[] = [
        'name',
        'billType',
        'action',
        'edit',
        'users',
        'additionalUsers',
    ];
    protected tasks: Task[];
    protected dataSource: MatTableDataSource<Task>;
    public editingTasks = false;
    private _flippedIndices: { [id: string]: boolean } = {};

    constructor(
        private clientService: ClientService,
        private taskService: TaskService,
        private dialog: MatDialog,
        private snackBar: UniversalSnackbarService,
    ) {}

    get project(): Project {
        return this._project();
    }

    set project(value: Project) {
        this._project.set(value);
    }

    get flippedIndices(): { [id: string]: boolean } {
        return this._flippedIndices;
    }

    protected toggleFlip(taskId: string) {
        this.flippedIndices[taskId] = !Boolean(this.flippedIndices[taskId]);
    }

    protected userConcatName(user: TaskUser, $index: number): string {
        return `User#${$index + 1}: ${user.userProfile.firstName} ${user.userProfile.lastName}`;
    }

    protected tooltipText(users: TaskUser[], $index: number): string {
        return users
            .map((user, index) => this.userConcatName(user, $index + index))
            .join('\n');
    }

    protected dontSort(columnName: string) {
        return this.tasks.every(
            (task) => task[columnName] === this.tasks[0][columnName],
        );
    }

    protected addTask(): void {
        const dialogRef = this.dialog.open(UpsertTaskComponent, {
            data: {
                title: 'New Task',
                task: null,
                clientName: this.client.name,
                projectName: this.project.name,
                projectId: this.project.id,
                approverRequired: false,
            },
        });

        dialogRef.afterClosed().subscribe((val) => {
            if (val) {
                this.taskAction = TaskAction.CREATE;
                this.updateTasks(this.project);
            }
        });
    }

    protected onActiveChange(event: MatSlideToggleChange): void {
        this.viewingActiveTasks = event.checked;
        this.updateTasks(this.project);
    }

    protected removeTask(task: Task): void {
        this.loading = true;
        task.status = INACTIVE;
        this.taskService.put([task.id], task).subscribe({
            next: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_DEACTIVATED);
                this.updateTasks(this.project);
            },
            error: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_DEACTIVATED_F);
            },
        });
    }

    protected makeTaskActive(task: Task): void {
        this.loading = true;
        task.status = ACTIVE;
        this.taskService.put([task.id], task).subscribe({
            next: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_ACTIVATED);
                this.updateTasks(this.project);
            },
            error: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_ACTIVATED_F);
            },
        });
    }

    protected editTask(task: Task): void {
        const dialogref = this.dialog.open(UpsertTaskComponent, {
            data: {
                title: 'Edit Task',
                task: task,
                clientName: this.client.name,
                projectName: this.project.name,
                projectId: this.project.id,
                approverRequired: task.approverRequired,
                billType: task.billType,
            },
        });

        dialogref.afterClosed().subscribe(() => {
            this.taskAction = TaskAction.UPDATE;
            this.updateTasks(this.project);
        });
    }

    protected editProjectTasks(event?: Event): void {
        if (!event) {
            this.editingTasks = true;
        } else {
            this.editingTasks = !this.editingTasks;
        }
    }

    protected updateTasks(project: Project): void {
        this.loading = true;
        if (!this.client) {
            this.clientService.get([project.clientId]).subscribe({
                next: (client: Client) => {
                    this.client = client;
                },
                error: () => {
                    this.snackBar.open(MSG.DATA_F);
                },
            });
        }

        const params = new Map<string, number | string>([
            [COUNT, COUNT_ALL],
            [PROJECTID, this.project.id],
        ]);

        this.taskService.get([], params).subscribe({
            next: (tasks: Page<Task>) => {
                this.loading = false;
                this.tasks = tasks.items;
                switch (this.taskAction) {
                    case TaskAction.CREATE:
                        this.snackBar.open(MSG.TASK_CREATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                    case TaskAction.UPDATE:
                        this.snackBar.open(MSG.TASK_UPDATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                }
            },
            error: () => {
                this.loading = false;
                switch (this.taskAction) {
                    case TaskAction.CREATE:
                        this.snackBar.open(MSG.TASK_CREATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                    case TaskAction.UPDATE:
                        this.snackBar.open(MSG.TASK_UPDATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                    case TaskAction.LOADING:
                        this.snackBar.open(MSG.TASK_LOADING_F);
                        this.taskAction = TaskAction.IDLE;
                        break;
                }
            },
        });
    }

    protected getDataSource(): MatTableDataSource<Task> {
        this.dataSource = new MatTableDataSource(this.tasks);
        this.dataSource.sort = this.sort;
        return this.dataSource;
    }

    public setProject(project: Project) {
        this.project = project;
        if (project) {
            this.taskAction = TaskAction.LOADING;
            this.updateTasks(project);
        }
    }
}
